import type { PersistQueryClientOptions, PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type { QueryClient, Query } from '@tanstack/react-query';
import type { AppQueryMeta } from '../queries/core/queryMeta';

const QUERY_CACHE_STORAGE_KEY = 'aa:react-query-cache:v1';
const QUERY_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const QUERY_CACHE_BUSTER = 'aa-build-v1';

let cachedPersister: Persister | null = null;

function isPlacementQuery(key: readonly unknown[] | undefined): boolean {
  return key?.[0] === 'public' && key?.[1] === 'placement';
}

function canUseStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const testKey = '__aa_query_cache_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function getPersister(): Persister | null {
  if (cachedPersister) return cachedPersister;
  if (!canUseStorage()) return null;

  cachedPersister = createSyncStoragePersister({
    storage: window.localStorage,
    key: QUERY_CACHE_STORAGE_KEY,
    throttleTime: 1000,
    deserialize: (serialized) => {
      const persisted = JSON.parse(serialized) as PersistedClient;
      // Older releases persisted guest answers and a relative timer. Never hydrate those.
      persisted.clientState.queries = persisted.clientState.queries.filter(query => !isPlacementQuery(query.queryKey));
      return persisted;
    },
  });

  return cachedPersister;
}

export function shouldPersistQuery(query: Query): boolean {
  if (isPlacementQuery(query.queryKey)) return false;
  const meta = (query.meta ?? {}) as AppQueryMeta;
  if (meta.sensitive) return false;
  if (meta.persist !== true) return false;
  return query.state.status === 'success';
}

export function getPersistQueryOptions(queryClient: QueryClient): PersistQueryClientOptions | null {
  const persister = getPersister();
  if (!persister) return null;

  return {
    queryClient,
    persister,
    maxAge: QUERY_CACHE_MAX_AGE_MS,
    buster: QUERY_CACHE_BUSTER,
    dehydrateOptions: {
      shouldDehydrateQuery: shouldPersistQuery,
      shouldDehydrateMutation: () => false,
    },
  };
}

export async function clearPersistedQueryCache(): Promise<void> {
  const persister = getPersister();
  if (!persister) return;
  await persister.removeClient();
}
